import { useState } from 'react';

import dayjs from 'dayjs';
import { DatatableWrapper, TableBody, TableColumnType, TableHeader } from 'react-bs-datatable';
import { Stack, Table } from 'react-bootstrap';

import InfoIconButton from '@components/Button/InfoIconButton';
import ExpandableSection from '@components/ExpandableSection/ExpandableSection';
import InfoDialog from '@components/Modals/InfoDialog';
import { Clinician } from '@customTypes/clinician';
import { IDraftOrder } from '@customTypes/draftOrder';
import { getClinicianName } from '@helpers/clinician';

import NoDraftsContent from '../NoDraftsContent/NoDraftsContent';

interface IProps {
  deletedDraftOrders: IDraftOrder[];
  loading: boolean;
}

export default function DeletedDrafts({ deletedDraftOrders, loading }: IProps) {
  const [showInfoDialog, setShowInfoDialog] = useState<boolean>(false);

  const headers: TableColumnType<IDraftOrder>[] = [
    {
      title: 'Draft Name',
      prop: 'name',
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Ordering Provider',
      prop: 'ordering_provider',
      cell: (row: IDraftOrder) => getClinicianName(row.ordering_provider),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Deleted By',
      prop: 'deleted_by',
      cell: (row: IDraftOrder) => getClinicianName(row.deleted_by),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Created By',
      prop: 'created_by',
      cell: (row: IDraftOrder) => getClinicianName(row.created_by),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Deleted On',
      prop: 'deleted_at',
      cell: row => dayjs.utc(row.deleted_at).local().format('MM/DD/YYYY'),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
  ];

  return (
    <>
      <ExpandableSection
        id='deletedDrafts'
        expandableContentId='deletedDraftsContent'
        headerTitle={
          <>
            <span>Deleted Drafts ({deletedDraftOrders.length})</span>
            <InfoIconButton onClick={() => setShowInfoDialog(true)} aria-label='Deleted Drafts Info Button' />
          </>
        }
        defaultIsCollapsed
        loading={loading}
      >
        {deletedDraftOrders.length === 0 ? (
          <NoDraftsContent content={'You have no deleted drafts'} />
        ) : (
          <DatatableWrapper
            body={deletedDraftOrders}
            headers={headers}
            sortProps={{
              initialState: { prop: 'deleted_at', order: 'desc' },
              columnValueProcessor: {
                name: (value: string) => value.toLowerCase(),
                ordering_provider: (clinician: Clinician) => getClinicianName(clinician),
                created_by: (clinician?: Clinician) => getClinicianName(clinician),
                deleted_by: (clinician?: Clinician) => getClinicianName(clinician),
              },
            }}
          >
            <Table className='table-with-sticky-header' responsive>
              <TableHeader />
              <TableBody />
            </Table>
          </DatatableWrapper>
        )}
      </ExpandableSection>
      {showInfoDialog && (
        <InfoDialog onClose={() => setShowInfoDialog(false)} title='Deleted Drafts'>
          <Stack gap={4}>
            <p className='mb-0'>Drafts deleted more than 30 days ago are not listed.</p>
          </Stack>
        </InfoDialog>
      )}
    </>
  );
}
